import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { canAccessRoute, getRouteAccessRule } from "Routes/routeAccess";
interface MenuItem {
  label: string;
  isHeader?: boolean;
  id?: string;
  icon?: string;
  link?: string;
  click?: (e: React.MouseEvent) => void;
  stateVariables?: boolean;
  subItems?: MenuItem[];
  allowedUserTypes?: string[];
  requiredPermission?: string | string[];
  requiredPlatformPermission?: string | string[];
}

interface NavdataProps {
  userRole: string;
}
interface JwtPayload {
  userType: string;
  permissions?: string[] | string;
  isOwner?: boolean;
  isSuperAdmin?: boolean;
  platformPermissions?: string[];
}

const Navdata: React.FC<NavdataProps> = ({ userRole }) => {
  const token = localStorage.getItem("authUser");
  let decoded: any = "";
  if (token !== null) {
    decoded = jwtDecode<JwtPayload>(token);
  } else {
    console.error("Token is null");
  }

  const parsedPermissions = Array.isArray(decoded.permissions)
    ? decoded.permissions
    : typeof decoded.permissions === "string"
      ? [decoded.permissions]
      : [];
  const permissions = parsedPermissions.map((permission:any) =>
    String(permission).trim()
  );
  const currentUserType = decoded?.userType || userRole;
  const isOwner = decoded?.isOwner === true;
  const isSuperAdmin = decoded?.isSuperAdmin === true;
  const platformPermissions = Array.isArray(decoded?.platformPermissions)
    ? decoded.platformPermissions
    : [];

  const hasPermission = (requiredPermission?: string | string[]) => {
    if (!requiredPermission) return true;
    if (currentUserType !== "contractor") return true;
    if (isOwner || permissions.includes("ALL")) return true;

    const requiredPermissions = Array.isArray(requiredPermission)
      ? requiredPermission
      : [requiredPermission];

    return requiredPermissions.some((permission) =>
      permissions.includes(permission)
    );
  };

  const hasAccess = (item: MenuItem) => {
    const routePath = item.link?.split("?")[0];
    const routeRule = routePath && routePath !== "/#"
      ? getRouteAccessRule(routePath)
      : undefined;

    if (routeRule) {
      return canAccessRoute(routeRule);
    }

    if (
      item.allowedUserTypes &&
      item.allowedUserTypes.length > 0 &&
      !item.allowedUserTypes.includes(currentUserType)
    ) {
      return false;
    }

    if (currentUserType === "admin" && item.requiredPlatformPermission) {
      const required = Array.isArray(item.requiredPlatformPermission)
        ? item.requiredPlatformPermission
        : [item.requiredPlatformPermission];
      if (!isSuperAdmin && !required.some((permission) => platformPermissions.includes(permission))) {
        return false;
      }
    }

    return hasPermission(item.requiredPermission);
  };

  const [isDashboard, setIsDashboard] = useState(false);
  const [isSupportTicket, setIsSupportTicket] = useState(false);
  const [isRealEstate, setIsRealEstate] = useState(false);
  const [isLeads, setIsLeads] = useState(false);
  const [isContractCat, setIsContractCat] = useState(false);
  const [isInsuarance, setInsurance] = useState(false);
  const [isContracts, setIsContracts] = useState(false);
  const [isMerchant, setIsMerchant] = useState(false);

  const [iscurrentState, setIscurrentState] = useState("Dashboard");

  function updateIconSidebar(e: React.MouseEvent) {
    if (e.target && (e.target as HTMLElement).getAttribute("sub-items")) {
      const ul = document.getElementById("two-column-menu");
      const iconItems = ul!.querySelectorAll(".nav-icon.active");
      let activeIconItems = Array.from(iconItems);
      activeIconItems.forEach((item) => {
        item.classList.remove("active");
        var id = (item as HTMLElement).getAttribute("sub-items");
        var menusId = document.getElementById(id!);
        if (menusId) {
          (menusId.parentElement as HTMLElement).classList.remove("show");
        }
      });
      (e.target as HTMLElement).classList.add("active");
    }
  }

  useEffect(() => {
    document.body.classList.remove("twocolumn-panel");

    if (iscurrentState !== "Dashboard") {
      setIsDashboard(false);
    }
    if (iscurrentState !== "Contracts") {
      setIsContracts(false);
    }
    if (iscurrentState !== "Support Ticket") {
      setIsSupportTicket(false);
    }
    if (iscurrentState !== "Real Estate") {
      setIsRealEstate(false);
    }
    if (iscurrentState !== "leads") {
      setIsLeads(false);
    }
    if (iscurrentState !== "contractCat") {
      setIsContractCat(false);
    }
    if (iscurrentState !== "Insurance") {
      setInsurance(false);
    }
    if (iscurrentState !== "Merchant") {
      setIsMerchant(false);
    }
  }, [
    iscurrentState,
    isDashboard,
    isRealEstate,
    isSupportTicket,
    isContracts,
    isLeads,
    isContractCat,
    isInsuarance,
    isMerchant
  ]);

  // Your existing menuItems array
  const menuItems: any = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: "ph-squares-four",
      link: "/#",
      allowedUserTypes: ["admin", "contractor", "homeowner", "real_estate_agent", "broker", "subcontractor"],
      requiredPermission: ["View Dashboard", "VIEW_DASHBOARD"],
      requiredPlatformPermission: currentUserType === "admin" ? "PLATFORM_DASHBOARD_VIEW" : undefined,
      click: function (e: any) {
        e.preventDefault();
        setIsDashboard(!isDashboard);
        setIscurrentState("Dashboard");
        updateIconSidebar(e);
      },
      stateVariables: isDashboard,
    },
    {
      id: "dashboard",
      label: "Profile",
      link: "/pages-profile-settings",
      icon: "ph-user-circle-gear",
      allowedUserTypes: ["contractor", "homeowner", "real_estate_agent", "broker", "subcontractor", "inspector"],
      parentId: "pages",
    },
    {
      id: "cardsExpiringSoon",
      label: "Cards Expiring Soon",
      icon: "ph-credit-card",
      link: "/billing/cards-expiring-soon",
      allowedUserTypes: ["admin"],
      requiredPermission: "",
      requiredPlatformPermission: "BILLING_VIEW",
    },
    {
      id: "paymentAlerts",
      label: "Payment Alerts",
      icon: "ph-warning-circle",
      link: "/billing/payment-alerts",
      allowedUserTypes: ["admin"],
      requiredPermission: "",
      requiredPlatformPermission: "BILLING_VIEW",
    },
    {
      id: "twilioNotifications",
      label: "Twilio Notifications",
      icon: "ph-phone-call",
      link: "/twilio-notifications",
      allowedUserTypes: ["admin"],
      requiredPermission: "",
      requiredPlatformPermission: "NOTIFICATION_SEND",
    },
    {
      id: "freeEstimates",
      label: "Free Estimates",
      icon: "ph-clipboard-text",
      link: "/free-estimates",
      allowedUserTypes: ["admin"],
      requiredPermission: "",
      requiredPlatformPermission: "COMPANY_VIEW",
    },
    {
      id: "insuranceContacts",
      label: "Insurance Contacts",
      icon: "ph-shield-check",
      link: "/insurance-contacts",
      allowedUserTypes: ["admin"],
      requiredPermission: "",
      requiredPlatformPermission: "COMPLIANCE_VIEW",
    },
    {
      id: "mechanicsLienIntakes",
      label: "Mechanics Lien Intakes",
      icon: "ph-file-text",
      link: "/mechanics-lien-intakes",
      allowedUserTypes: ["admin", "contractor", "testContractor", "subcontractor"],
      requiredPermission: "",
    },
 
    currentUserType === "homeowner" && {
      id: "projects",
      label: "Projects",
      icon: "ph-briefcase",
      link: "/pages-projects",
      allowedUserTypes: ["homeowner"],
    },
    {
      id: "projects",
      label: "Recent Contractors",
      icon: "ph-clock-counter-clockwise",
      link: "/recent-contractors",
      allowedUserTypes: ["homeowner"],
    },
    {
      id: "projects",
      label: "Suggested Contractors",
      icon: "ph-user-plus",
      link: "/suggested-contractors",
      allowedUserTypes: ["homeowner"],
    },
    {
      id: "homeownerRecommendedProperties",
      label: "Recommended Properties",
      icon: "ph-house-simple",
      link: "/recommended-properties",
      allowedUserTypes: ["homeowner"],
    },
    //admin
    {
      id: "adminContractors",
      label: "Contractors",
      icon: "mdi mdi-account-hard-hat",
      link: "/pages-all-contractors",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "COMPANY_VIEW",
    },
    {
      id: "adminHomeowners",
      label: "All Homeowners",
      icon: "ph-house-line",
      link: "/pages-all-homeowners",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "USER_VIEW",
    },
    {
      id: "adminHomeowners",
      label: "Escrow Requests",
      icon: "ph-wallet",
      link: "/escrow-requests",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "BILLING_VIEW",
    },
    {
      id: "wallet",
      label: currentUserType === "admin" ? "Contractor Wallets" : "My Wallet",
      icon: "ph-wallet",
      link: "/wallet",
      allowedUserTypes: ["admin", "contractor", "testContractor"],
      requiredPlatformPermission: currentUserType === "admin" ? "BILLING_VIEW" : undefined,
    },
    {
      id: "contractor-referrals",
      label: "Refer & Earn",
      icon: "ph-gift",
      link: "/wallet?tab=referrals",
      allowedUserTypes: ["contractor", "testContractor"],
    },
    {
      id: "adminHomeowners",
      label: "All Leads",
      icon: "ph-target",
      link: "/pages-all-leads",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "LEAD_VIEW",
    },
    {
      id: "adminHomeowners",
      label: "Category Master",
      icon: "ph-folders",
      link: "/catogery-master",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "CONTENT_MANAGE",
    },
    {
      id: "adminHomeowners",
      label: "Subcategory Master",
      icon: "ph-folder-notch-open",
      link: "/sub-catogery-master",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "CONTENT_MANAGE",
    },
    {
      id: "adminHomeowners",
      label: "Coupon Dashboard",
      icon: "ph-ticket",
      link: "/coupan-dashboard",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "COUPON_MANAGE",
    },
    {
      id: "adminHomeowners",
      label: "Version Control",
      icon: "ph-git-branch",
      link: "/version-controle",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "APP_VERSION_MANAGE",
    },
    {
      id: "adminHomeowners",
      label: "Push Notifications",
      icon: "ph-bell-ringing",
      link: "/push-notification",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "NOTIFICATION_SEND",
    },
    {
      id: "adminMlsCache",
      label: "MLS Cache Control",
      icon: "ph-database",
      link: "/mls-cache-control",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "CONTENT_MANAGE",
    },
    {
      id: "adminPropertyLeads",
      label: "Property Leads",
      icon: "ph-house-line",
      link: "/property-leads",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "LEAD_VIEW",
    },
    {
      id: "adminRealestateAgents",
      label: "Real Estate Agents",
      icon: "ph-buildings",
      link: "/pages-all-real-estate-agents",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "USER_VIEW",
    },
    {
      id: "adminAdminsData",
      label: "Platform Access",
      icon: "ph-shield-check",
      link: "/platform-access",
      allowedUserTypes: ["admin"],
      requiredPlatformPermission: "PLATFORM_STAFF_VIEW",
    },

    //admin
    // {
    //   id: "adminForms",
    //   label: "TTC Forms Data",
    //   icon: "ph-files",
    //   link: "/pages-all-forms",
    //   allowedUserTypes: ["admin"],
    // },

    //admin
    // {
    //   id: "adminSubscribers",
    //   label: "Subscribers",
    //   icon: "ph-users-three",
    //   link: "/pages-all-subscribers",
    //   allowedUserTypes: ["admin"],
    // },
    {
      id: "leads",
      label: "Leads",
      icon: "ph-target",
      link: "/#",
      allowedUserTypes: ["contractor"],
      click: function (e: any) {
        e.preventDefault();
        setIsLeads(!isLeads);
        setIscurrentState("leads");
        updateIconSidebar(e);
      },
      stateVariables: isLeads,
      subItems: [
        {
          id: "leads",
          label: "Leads Pro",
          link: "/pages-projects",
          parentId: "leads",
          allowedUserTypes: ["contractor"],
          requiredPermission: "VIEW_JOBS",
        },
        {
            id: "reqLeads",
            label: "Requested Leads",
            link: "/project-requested",
          parentId: "leads",
          allowedUserTypes: ["contractor"],
          requiredPermission: "VIEW_JOBS",
        },
        {
          id: "ongoingLeads",
          label: "Accepted Leads",
          link: "/project-ongoing-leads",
          parentId: "leads",
          allowedUserTypes: ["contractor"],
          requiredPermission: "VIEW_JOBS",
        },
        {
          id: "ongoingLeads",
          label: "Lead History",
          link: "/leadHistory",
          parentId: "leads",
          allowedUserTypes: ["contractor"],
          requiredPermission: "VIEW_JOBS",

        },
      ],
    },
  
    {
      id: "createContracts",
      label: "Contracts",
      icon: "ph-file",
      link: currentUserType === "contractor" ? "/get-contracts" : "/#",
      allowedUserTypes: ["admin", "contractor", "homeowner", "subcontractor", "inspector"],
      requiredPermission: currentUserType === "contractor" ? "VIEW_CONTRACT" : undefined,
      click: currentUserType === "contractor" ? undefined : function (e: any) {
        e.preventDefault();
        setIsContractCat(!isContractCat);
        setIscurrentState("contractCat");
        updateIconSidebar(e);
      },
      stateVariables: isContractCat,
      subItems: currentUserType === "contractor" ? undefined : [
        {
          id: "createContracts",
          label: "Contract Dashboard",
          link: "/get-contracts",
          parentId: "createContracts",
          allowedUserTypes: ["contractor", "homeowner", "inspector","admin"],
          requiredPermission: "VIEW_CONTRACT",
        },
      ],
    },
    {
      id: "invoice",
      label: "Team",
      icon: "ph-users-three",
      link: "/team_management",
      allowedUserTypes: ["contractor"],
      requiredPermission: ["VIEW_COMPANY_STAFF", "MANAGE_COMPANY_STAFF",  "MANAGE_TEAM"],

    
   
    },
    {
      id: "invoice",
      label: "Insurance and Payments",
      icon: "ph-credit-card",
      link: "/#",
      allowedUserTypes: ["contractor"],
      click: function (e: any) {
        e.preventDefault();
        setInsurance(!isInsuarance);
        setIscurrentState("Insurance");
        updateIconSidebar(e);
      },
      stateVariables: isInsuarance,
      subItems: [
      
        {
          id: "listview",
          label: "Credit card processor",
          icon: "ph-file-text",
          link: "/credit_card_precessing",
          onClick: "https://www.deluxe.com/mkt/referrals/ttc-usa/",
          parentId: "invoice",
          allowedUserTypes: ["contractor"],
          requiredPermission: "",
        },
        {
          id: "listview",
          label: "P&C and Workers Comp",
          icon: "ph-file-text",
          link: "/property_insurance",
          parentId: "invoice",
          allowedUserTypes: ["contractor"],
          requiredPermission: "",
        },
        {
          id: "listview",
          label: "Health and Life",
          link: "/life_health_insurance",
          parentId: "invoice",
          allowedUserTypes: ["contractor"],
          requiredPermission: "",
        },
        {
          id: "listview",
          label: "Investments",
          link: "/investments",
          parentId: "invoice",
          allowedUserTypes: ["contractor"],
          requiredPermission: "",
        },
      ],
    },
    {
      id: "merchantEnrollment",
      label: "Merchant Enrollment",
      icon: "ph-credit-card",
      link: "/#",
      allowedUserTypes: ["admin", "contractor"],
      click: function (e: any) {
        e.preventDefault();
        setIsMerchant(!isMerchant);
        setIscurrentState("Merchant");
        updateIconSidebar(e);
      },
      stateVariables: isMerchant,
      subItems: [
        {
          id: "merchantBusinessInfo",
          label: "Business Info",
          link: "/merchant/business-info",
          parentId: "merchantEnrollment",
          allowedUserTypes: ["admin", "contractor"],
          requiredPermission: "",
        },
        {
          id: "merchantOwners",
          label: "Owners",
          link: "/merchant/owners",
          parentId: "merchantEnrollment",
          allowedUserTypes: ["admin", "contractor"],
          requiredPermission: "",
        },
        {
          id: "merchantKyc",
          label: "KYC",
          link: "/merchant/kyc",
          parentId: "merchantEnrollment",
          allowedUserTypes: ["admin", "contractor"],
          requiredPermission: "",
        },
        {
          id: "merchantStatus",
          label: "Status",
          link: "/merchant/status",
          parentId: "merchantEnrollment",
          allowedUserTypes: ["admin", "contractor"],
          requiredPermission: "",
        },
        {
          id: "merchantPaymentSetup",
          label: "Payment Setup",
          link: "/merchant/payment-setup",
          parentId: "merchantEnrollment",
          allowedUserTypes: ["admin", "contractor"],
          requiredPermission: "",
        },
        {
          id: "merchantPayments",
          label: "Payments",
          link: "/merchant/payments",
          parentId: "merchantEnrollment",
          allowedUserTypes: ["admin", "contractor"],
          requiredPermission: "",
        },
        {
          id: "merchantPayouts",
          label: "Payouts",
          link: "/merchant/payouts",
          parentId: "merchantEnrollment",
          allowedUserTypes: ["admin", "contractor"],
          requiredPermission: "",
        },
      ],
    },
 
  
    // {
    //   id: "liveContracts",
    //   label: "Live Contracts",
    //   icon: "ph-handshake",
    //   link: "/live-contracts",
    //   allowedUserTypes: [],
    //   requiredPermission: "",
    // },

    {
      id: "contractor-invoices",
      label: "Invoices",
      icon: "ph-receipt",
      link: "/invoices-list",
      allowedUserTypes: ["contractor"],
      requiredPermission: "MANAGE_BILLING",
    },

    // {
    //   id: "createSwornContracts",
    //   label: "Contracts Sworn Contracts",
    //   icon: "ph-certificate",
    //   link: "/create-sworn-contracts",
    //   allowedUserTypes: ["admin"],
    // },

 


    {
      id: "apps-chat",
      label: "Chat",
      icon: "ph-chats",
      link: "/apps-chat",
      allowedUserTypes: ["admin", "contractor", "homeowner", "real_estate_agent", "real_esate_agent", "broker"],
      requiredPermission: ["CHAT_ACCESS"],
    },
    {
      id: "logan-ai",
      label: "Logan AI",
      icon: "ph-sparkle",
      link: "/logan-ai",
      allowedUserTypes: ["admin", "contractor", "testContractor", "homeowner", "real_estate_agent", "real_esate_agent", "broker", "subcontractor", "inspector"],
    },
    currentUserType === "homeowner" &&
    {
      id: "property-manager",
      label: "Properties",
      icon: "ph-house-line",
      link: "/property-manager",
      allowedUserTypes: ["homeowner"],
    },

    // {
    //   id: "departments",
    //   label: "Departments",
    //   icon: "ph-buildings",
    //   link: "/#",
    //   allowedUserTypes: ["admin"],
    //   click: function (e: any) {
    //     e.preventDefault();
    //     setIsContracts(!isContracts);
    //     setIscurrentState("Contracts");
    //     updateIconSidebar(e);
    //   },
    //   stateVariables: isContracts,
    //   subItems: [
    //     {
    //       id: "legel",
    //       label: "Legel",
    //       link: "/pages-crm",
    //       parentId: "contracts",
    //       allowedUserTypes: ["admin"],
    //     },
    //     {
    //       id: "crm",
    //       label: "crm",
    //       link: "/pages-crm",
    //       parentId: "contracts",
    //       allowedUserTypes: ["admin"],
    //     },
    //     {
    //       id: "sales",
    //       label: "Sales",
    //       link: "/pages-crm",
    //       parentId: "contracts",
    //       allowedUserTypes: ["admin"],
    //     },
    //     {
    //       id: "customerService",
    //       label: "Customer Service",
    //       link: "/pages-crm",
    //       parentId: "contracts",
    //       allowedUserTypes: ["admin"],
    //     },
    //     {
    //       id: "finance",
    //       label: "Finance",
    //       link: "/pages-crm",
    //       parentId: "contracts",
    //       allowedUserTypes: ["admin"],
    //     },
    //   ],
    // },

    {
      id: "agentPropertyLeads",
      label: "Available Property Leads",
      icon: "ph-house-line",
      link: "/agent-property-leads",
      allowedUserTypes: ["real_estate_agent", "broker"],
    },
    {
      id: "agentMyPropertyLeads",
      label: "My Property Leads",
      icon: "ph-briefcase",
      link: "/agent-my-property-leads",
      allowedUserTypes: ["real_estate_agent", "broker"],
    },
    {
      id: "agentHomeowners",
      label: "Add Homeowner",
      icon: "ph-house-line",
      link: "/apps-real-estate-agent-homeowner",
      allowedUserTypes: ["real_estate_agent", "broker"],
    },
    {
      id: "agentPropertyRecommendations",
      label: "Property Recommendations",
      icon: "ph-folders",
      link: "/agent-property-recommendations",
      allowedUserTypes: ["real_estate_agent", "broker"],
    },
    {
      id: "agentExploreContractors",
      label: "Explore Contractors",
      icon: "ph-buildings",
      link: "/apps-real-estate-explore-contractor",
      allowedUserTypes: ["real_estate_agent", "broker"],
    },
    {
      id: "agentEarnings",
      label: "Earnings",
      icon: "ph-currency-dollar",
      link: "/apps-real-estate-earning",
      allowedUserTypes: ["real_estate_agent", "broker"],
    },
  
    // {
    //   id: "contacts",
    //   label: "Home Owners Contacts",
    //   icon: "ph-address-book",
    //   link: "/pages-contacts",
    //   parentId: "pages",
    //   allowedUserTypes: ["admin"],
    // },

  
    
    {
      id: "marketing",
      label: "Marketing & Website Development",
      icon: "ph-megaphone",
      link: "/marketing",
      parentId: "pages",
      allowedUserTypes: ["contractor"],
    },
  
   
   
    // {
    //   id: "pages",
    //   label: "Settings",
    //   icon: "ph-address-book",
    //   link: "/#",
    //   click: function (e: any) {
    //     e.preventDefault();
    //     setIsPages(!isPages);
    //     setIscurrentState("Pages");
    //     updateIconSidebar(e);
    //   },
    //   stateVariables: isPages,
    //   subItems: [
    //     {
    //       id: "profile",
    //       label: "Profile",
    //       link: "/pages-profile",
    //       parentId: "pages",
    //     },
    //     {
    //       id: "profilesettings",
    //       label: "Profile Settings",
    //       link: "/pages-profile-settings",
    //       parentId: "pages",
    //     },
    //   ],
    // },
    {
      id: "support-ticket",
      label: "Support",
      icon: "ph-ticket",
      link: currentUserType === "admin" ? "/#" : "/apps-tickets-list",
      allowedUserTypes: ["admin", "contractor", "testContractor", "homeowner"],
      click: currentUserType === "admin" ? function (e: any) {
        e.preventDefault();
        setIsSupportTicket(!isSupportTicket);
        setIscurrentState("Support Ticket");
        updateIconSidebar(e);
      } : undefined,
      stateVariables: isSupportTicket,
      subItems: currentUserType === "admin" ? [
        {
          id: "listview-support",
          label: "List View",
          link: "/apps-tickets-list",
          parentId: "support-ticket",
          allowedUserTypes: ["admin", "contractor", "testContractor", "homeowner"],
        },
        {
          id: "overview",
          label: "Overview",
          link: "/apps-tickets-overview",
          parentId: "support-ticket",
          allowedUserTypes: ["admin", ],
        },
        {
          id: "logan-ai-admin",
          label: "Logan AI Administration",
          link: "/logan-ai-admin",
          parentId: "support-ticket",
          allowedUserTypes: ["admin"],
        },
      ] : undefined,
    },
  ];


  // Render only allowed menu items
  const filteredMenuItems = menuItems.filter(Boolean).filter((item: MenuItem) => {
    if (item.isHeader) {
      return hasAccess(item);
    }

    if (item.subItems) {
      if (!hasAccess(item)) {
        return false;
      }
      // Filter subItems
      item.subItems = item.subItems.filter((subItem: MenuItem) =>
        hasAccess(subItem)
      );
      return item.subItems.length > 0; // Keep parent item if it has allowed subItems
    }

    return hasAccess(item);
  });

  return <React.Fragment>{filteredMenuItems}</React.Fragment>;
};

export default Navdata;
